import {
  DataTableDataItem,
  DataTableDataItemValue,
  IDataTable,
  IElementDataTableProperty,
} from '../../models/data-table.model';
import { validateDataTableData } from '@shared/validation/data-table.validation';
import { IElementProperty } from '@shared/models/element-property.model';
import { guards } from '../typeguards.helpers';
import { OneOfPropertyElements } from '@shared/models/project.model';
import { getElementProperties } from '../element-property.helpers';
import { mapKeys, uniq } from 'lodash';
import { isOneOf } from '../array.helpers';
import { ItemOrItemId } from '@shared/models/type-helpers.model';
import { getId } from '../object.helpers';
import { formatExpressionVariableName } from '../expression-variables.helpers';

/**
 * Parse the JSON and validate it (if not already parsed)
 * @param input Either a string or an already parsed array
 * @returns A parsed and validated DataTableDataItem array
 */
export const parseDataTableData = (
  input: DataTableDataItem[] | string,
): IDataTable['data'] => {
  const parsedJson = guards.is.string(input)
    ? JSON.parse(input, (key, value) => {
        return guards.is.null(value) ? undefined : value; // Replace null with undefined
      })
    : input;
  return validateDataTableData(mapDataTableKeys(parsedJson));
};

/**
 * Map the keys of the data-table json data to expression variable names
 * @param data
 * @returns
 */
const mapDataTableKeys = (data: DataTableDataItem[]): Record<string, any>[] => {
  return data.map((item) => {
    return mapKeys(item, (value, key) => formatExpressionVariableName(key));
  });
};

/**
 * Check if a value is a valid data table data item value.
 * @param item
 * @returns
 */
export const isDataTableDataItemValue = (
  item: unknown,
): item is DataTableDataItemValue => {
  return guards.is.string(item) || guards.is.number(item); // TODO: Add boolean support
};

/**
 * Get the data table id from a property. Undefined if not a data table property.
 * @param property
 * @returns
 */
export const getDataTableIdFromProperty = (
  property: IElementProperty,
): string | undefined => {
  if ('table_id' in property) {
    return property.table_id as string;
  }
};

/**
 * Check if a property is a data table property. IE have a table_id referencing a data table
 * @param property
 * @returns
 */
export const isDataTableProperty = (
  property: IElementProperty | undefined,
): property is IElementDataTableProperty => {
  return (
    guards.is.defined(property) &&
    getDataTableIdFromProperty(property) !== undefined
  );
};

/**
 * Check if a property is a data table main property.
 * The main property is the one controlling what is selected
 * @param property
 * @returns
 */
export const isDataTableMainProperty = (
  property: IElementProperty | undefined,
): property is IElementDataTableProperty => {
  return isDataTableProperty(property) && !property.indentation;
};

/**
 * Check if a property is a data table child property
 * @param property
 * @returns
 */
export const isDataTableChildProperty = (
  property: IElementProperty | undefined,
): property is IElementDataTableProperty => {
  return isDataTableProperty(property) && property.indentation === 1;
};

/**
 * Get data table properties from an element by data table id
 * @param element
 * @param tablesOrIds Ids of the datatable, provide multiple to get properties from multiple data tables
 * @returns
 */
export const getPropertiesByDataTableId = (
  element: Parameters<typeof getElementProperties>[0],
  ...tablesOrIds: ItemOrItemId<IDataTable>[]
): IElementDataTableProperty[] => {
  const ids = tablesOrIds.map((tableOrId) => getId(tableOrId));
  return getElementProperties(element)
    .filter(isDataTableProperty)
    .filter((property) => isOneOf(ids, getDataTableIdFromProperty(property)));
};

/**
 * Get ids of all data tables connected to an element
 * @param element
 * @returns
 */
export const getConnectedDataTablesIdsFromElement = (
  element: OneOfPropertyElements,
): string[] => {
  return uniq(
    getElementProperties(element)
      .filter(isDataTableProperty)
      .map((property) => getDataTableIdFromProperty(property))
      .filter(guards.is.defined),
  );
};

/**
 * Whether a data table has been soft-deleted and should be hidden from selection UI.
 */
export const isDeletedDataTable = (dataTable: IDataTable): boolean =>
  dataTable.deleted_at != null;

/**
 * Non-deleted data tables for list views and "connect data table" selection.
 * Deleted tables remain in the full store set for enrich / expression resolution.
 */
export const getActiveDataTables = (
  dataTables: IDataTable[] | undefined,
): IDataTable[] =>
  (dataTables ?? []).filter((table) => !isDeletedDataTable(table));
